import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { Card, CardProps, CardHeader, CardFooter } from './Card'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

// const DialogClose = DialogPrimitive.Close

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      '[&>[data-slot=icon]]:size-4 [&>[data-slot=icon]]:text-txt-secondary',
      className,
    )}
    {...props}
  />
))
DialogClose.displayName = DialogPrimitive.Close.displayName

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    data-slot="dialog-body"
    ref={ref}
    className={cn('flex-1 overflow-auto px-4 py-4 text-sm', className)}
    {...props}
  />
))

// const DialogBody = React.forwardRef<
//   React.ElementRef<typeof ScrollArea.ScrollArea>,
//   React.ComponentPropsWithoutRef<typeof ScrollArea.ScrollArea>
// >(({ className, children, ...props }, ref) => (
//   <ScrollArea.ScrollArea
//     ref={ref}
//     className={cn('h-screen', className)}
//     {...props}
//   >
//     {children}
//   </ScrollArea.ScrollArea>
// ))

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    absolute?: boolean
  }
>(({ className, absolute, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      `${absolute ? 'absolute' : 'fixed'}`,
      'pointer-events-none inset-0 z-50 bg-modal-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  Omit<CardProps, 'ref'> & {
    containerClassName?: string
    absolute?: boolean
    containerId?: string
    customOverlay?: boolean
  }

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      as,
      containerId,
      absolute = true,
      customOverlay = false,
      ...props
    },
    ref,
  ) => (
    <DialogPortal
      {...(containerId
        ? { container: document.getElementById(containerId) }
        : {})}
      // container={document.getElementById('portal-middle')}
    >
      <DialogOverlay absolute={absolute} />
      <div>
        {customOverlay && (
          <div className="absolute inset-0 z-50 bg-modal-overlay"></div>
        )}
      </div>

      <DialogPrimitive.Content
        ref={ref}
        asChild
        tabIndex={-1}
        className={cn(
          `${absolute ? 'absolute' : 'fixed'} `,
          'left-[50%] top-[50%] z-50 flex max-h-[90vh] w-full max-w-[95vw] translate-x-[-50%] translate-y-[-50%] flex-col overflow-auto rounded-lg border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] md:max-w-md',
          className,
        )}
        {...props}
      >
        <Card
          as={as}
          className={cn(
            'py-4 [&:has([data-slot=dialog-footer])]:pb-0 [&:has([data-slot=dialog-header])]:pt-0 [&>[data-slot=dialog-body]]:pt-1.5 [&>[data-slot=separator]+[data-slot=dialog-body]]:pt-2.5',
          )}
        >
          {children}
        </Card>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)

DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <CardHeader
    data-slot="dialog-header"
    className={cn(
      'flex min-h-[48px] flex-none flex-row items-center justify-between overflow-auto  px-3 py-3 font-medium',
      className,
    )}
    {...props}
  />

  // <div
  //   data-slot="dialog-header"
  //   className={cn(
  //     'flex flex-col space-y-1.5 text-center sm:text-left',
  //     className,
  //   )}
  //   {...props}
  // />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <CardFooter
    data-slot="dialog-footer"
    className={cn(
      'flex flex-row justify-end gap-2 border-t-0 px-4 py-4',
      className,
    )}
    {...props}
  />
  // <div
  //   data-slot="dialog-footer"
  //   className={cn(
  //     'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
  //     className,
  //   )}
  //   {...props}
  // />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'font-inter text-base font-medium leading-none tracking-tight text-txt-primary',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogBody,
}
